import type { Sprite } from "pixi.js";
import { ActionPayloadValidationError } from "@slot/client-engine";
import type { ChanceDetonationInput, ChanceDetonationTarget } from "./replay/DetonateChanceSymbolActionHandler";
import type { ChanceMarkersView } from "./ChanceMarkersView";
import type { ChanceRemovalEffect } from "./ChanceRemovalEffect";
import { CHANCE_TUNING } from "./chanceTuning";
import type { GridCells, Log, PresentationTaskRunner, SymbolDimExposurePort } from "@slot/presentation";

/**
 * ChanceDetonationPresenter — presents AND performs `detonate_chance_symbol` (a GAME-DEFINED action, dispatched by
 * `replay/DetonateChanceSymbolActionHandler`): the barrel blows and takes its marked neighbours with it.
 *
 * ONE timeline (`CHANCE_TUNING`): every cell outside the blast dims and markers fade in behind the targets one after
 * another, clockwise around the barrel; a beat after the last, the barrel's Spine `win` (its explosion) starts in place
 * on the source cell. `removalStartSeconds` into the clip the blast's cells are removed through the mechanic (the
 * removal a win's `remove_symbols_by_mask` runs, with its post-removal hold), so the targets vanish on the barrel's
 * frame, while the barrel's own cell leaves when its clip ends (`ChanceRemovalEffect`) and the dim lifts then. It
 * resolves when the removal is done, so the next `collapse` drops into the hole. Distinct code from the win highlight
 * (no cannon, no meter, no label, no win counter).
 *
 * The removal is the action's state change, not decoration: it runs whatever the visuals (no Spine barrel, no markers).
 */
export class ChanceDetonationPresenter implements ChanceDetonationTarget {
  private readonly gridView: GridCells;
  private readonly runner: PresentationTaskRunner;
  private readonly cellSize: () => { width: number; height: number };
  private readonly markers: ChanceMarkersView | null;
  private readonly dimPort: SymbolDimExposurePort | null;
  private readonly playSourceClip: (index: number) => number | null;
  private readonly removalEffect: ChanceRemovalEffect;
  private readonly removeCells: (positionMask: string) => Promise<void>;
  private readonly playSound: (soundId: string) => void;
  private readonly log: Log;

  public constructor(input: {
    gridView: GridCells;
    runner: PresentationTaskRunner;
    /** A cell's size in the grid's space: the markers are cut to it. */
    cellSize: () => { width: number; height: number };
    /** The markers behind the targets; null → the targets are not marked. */
    markers: ChanceMarkersView | null;
    /** Per-sprite exposure owner for the dim — the SAME primitive the win highlight uses. */
    dimPort: SymbolDimExposurePort | null;
    /** Starts the barrel's `win` in place on the source cell; its length in clip seconds, or null without a skeleton. */
    playSourceClip: (index: number) => number | null;
    /** The tumble's removal effect, through which the barrel's cell leaves with its clip. */
    removalEffect: ChanceRemovalEffect;
    /** The mechanic's removal of a mask, with its post-removal hold. */
    removeCells: (positionMask: string) => Promise<void>;
    playSound: (soundId: string) => void;
    log: Log;
  }) {
    this.gridView = input.gridView;
    this.runner = input.runner;
    this.cellSize = input.cellSize;
    this.markers = input.markers;
    this.dimPort = input.dimPort;
    this.playSourceClip = input.playSourceClip;
    this.removalEffect = input.removalEffect;
    this.removeCells = input.removeCells;
    this.playSound = input.playSound;
    this.log = input.log;
  }

  public async detonateChanceSymbol(input: ChanceDetonationInput): Promise<void> {
    const t = CHANCE_TUNING;
    const cellCount = this.gridView.gridColumnCount * this.gridView.gridRowCount;
    const source = Number(input.sourcePositionIndex);
    if (source >= cellCount) {
      throw new ActionPayloadValidationError("detonate_chance_symbol", "sourcePositionIndex", `Cell ${source} is outside the ${cellCount}-cell grid.`);
    }
    const targets = this.gridView.maskToIndices(input.targetMask);
    const blast = new Set<number>([source, ...targets]);
    this.log(`[Chance] detonate source=${source} targets=${targets.length}`);

    const dimmed = this.dimAllBut(blast);
    // The markers in the order they light up: clockwise around the barrel, from 12 o'clock.
    const sourceAt = this.gridView.getCellLocalPosition(source);
    const markedCells = targets
      .map((index) => this.gridView.getCellLocalPosition(index))
      .sort((a, b) => clockAngle(sourceAt, a) - clockAngle(sourceAt, b));
    this.markers?.show(markedCells, this.cellSize());

    // The markers fade in one after another; a beat after the last is in, the barrel's clip starts.
    await Promise.all(markedCells.map((_, order) => this.fadeMarkerIn(order, order * t.marker.staggerSeconds, t.marker.fadeInSeconds)));
    if (t.barrelDelaySeconds > 0) await this.runner.sleep(t.barrelDelaySeconds, t.scope);

    this.playSound(t.activationSoundId);
    const clipSeconds = this.playSourceClip(source);
    if (clipSeconds === null) this.log("[Chance] no barrel skeleton — the barrel shrinks and bursts with its targets");
    // Started with the clip, so it ends when the clip does: the runner scales the wait as the clip is scaled.
    const clipEnd = clipSeconds === null ? null : this.runner.sleep(clipSeconds, t.scope);

    // The removal starts `removalStartSeconds` into the clip, so the targets vanish on the barrel's frame.
    await this.runner.sleep(Math.min(t.removalStartSeconds, clipSeconds ?? t.removalStartSeconds), t.scope);

    // The blast: the targets vanish on the barrel's frame and the markers fade out with them; the barrel's own cell
    // leaves when its clip ends, and the dim lifts then.
    const blastMask = Array.from({ length: cellCount }, (_, index) => (blast.has(index) ? "1" : "0")).join("");
    const markersGone = this.fadeMarkersOut(t.marker.fadeOutSeconds).then(() => this.markers?.clear());
    const dimLifted = (clipEnd ?? Promise.resolve()).then(() => this.liftDim(dimmed));
    const removed = clipEnd
      ? this.removalEffect.withBarrelExit(source, clipEnd, () => this.removeCells(blastMask))
      : this.removeCells(blastMask);
    await Promise.all([removed, markersGone, dimLifted]);
  }

  /** Dims every cell outside the blast; returns the sprites dimmed, for the lift. */
  private dimAllBut(blast: ReadonlySet<number>): Sprite[] {
    const t = CHANCE_TUNING;
    const dimPort = this.dimPort;
    if (!dimPort || t.dimExposure >= 0) return [];
    const dimmed: Sprite[] = [];
    const cellCount = this.gridView.gridColumnCount * this.gridView.gridRowCount;
    for (let index = 0; index < cellCount; index++) {
      if (blast.has(index)) continue;
      const sprite = this.gridView.getSymbolViewAtIndex(index)?.symbolSprite;
      if (!sprite) continue;
      dimPort.rampExposure(sprite, t.dimExposure, t.dimFadeSeconds, t.dimFadeEasing);
      dimmed.push(sprite);
    }
    return dimmed;
  }

  private liftDim(dimmed: readonly Sprite[]): void {
    const t = CHANCE_TUNING;
    for (const sprite of dimmed) {
      this.dimPort?.rampExposure(sprite, 0, t.dimFadeSeconds, t.dimFadeEasing, () => this.dimPort?.release(sprite));
    }
  }

  /** One marker's fade-in, `delay` seconds after the detonation starts, on engine time; nothing without markers. */
  private async fadeMarkerIn(order: number, delay: number, seconds: number): Promise<void> {
    const markers = this.markers;
    if (!markers) return;
    if (delay > 0) await this.runner.sleep(delay, CHANCE_TUNING.scope);
    await this.runner.tweenProgress(seconds, (progress) => markers.setMarkerOpacity(order, progress), CHANCE_TUNING.scope);
  }

  /** The whole set's fade-out, on engine time; resolves at once without markers. */
  private fadeMarkersOut(seconds: number): Promise<void> {
    const markers = this.markers;
    if (!markers) return Promise.resolve();
    return this.runner.tweenProgress(seconds, (progress) => markers.setOpacity(1 - progress), CHANCE_TUNING.scope);
  }
}

/** Where `cell` sits around `centre`, clockwise from 12 o'clock (y grows down), in [0, 2π). */
function clockAngle(centre: { x: number; y: number }, cell: { x: number; y: number }): number {
  const angle = Math.atan2(cell.x - centre.x, centre.y - cell.y);
  return angle < 0 ? angle + 2 * Math.PI : angle;
}
